import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  AppBar,
  Toolbar,
  Container,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Delete,
  Visibility,
  VisibilityOff,
  AccessTime,
  ThumbUp,
  Comment as CommentIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface Comment {
  id: string;
  postId: string;
  userId: string;
  username: string;
  userPhotoURL: string;
  text: string;
  likeCount: number;
  createdAt: any;
  updatedAt: any;
  parentCommentId?: string;
  isHidden: boolean;
  reportCount: number;
}

const CommentsManagementPage: React.FC = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  
  const [comments, setComments] = useState<Comment[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalComments, setTotalComments] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
      return;
    }
    loadComments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, navigate, page, rowsPerPage]);

  const loadComments = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/comments`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage
        }
      });
      
      if (response.data.success) {
        setComments(response.data.data.comments || []);
        setTotalComments(response.data.data.total || 0);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
      showSnackbar('Yorumlar yüklenirken hata oluştu', 'error');
    }
  };

  const handleDeleteComment = async () => {
    if (!selectedComment) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_BASE_URL}/api/comments/${selectedComment.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setComments(comments.filter((comment: Comment) => comment.id !== selectedComment.id));
      setDeleteDialogOpen(false);
      setSelectedComment(null);
      showSnackbar('Yorum başarıyla silindi', 'success');
    } catch (error) {
      console.error('Error deleting comment:', error);
      showSnackbar('Yorum silinirken hata oluştu', 'error');
    }
  };

  const handleToggleCommentVisibility = async (comment: Comment) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API_BASE_URL}/api/comments/${comment.id}/visibility`, {
        isHidden: !comment.isHidden
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setComments(comments.map((c: Comment) =>
        c.id === comment.id ? { ...c, isHidden: !c.isHidden } : c
      ));
      showSnackbar(
        comment.isHidden ? 'Yorum görünür hale getirildi' : 'Yorum gizlendi',
        'success'
      );
    } catch (error) {
      console.error('Error toggling comment visibility:', error);
      showSnackbar('Yorum durumu değiştirilirken hata oluştu', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Tarih yok';
    
    let date;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleString('tr-TR');
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            mr: 2,
            width: 40,
            height: 40
          }}>
            <CommentIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ 
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Yorum Yönetimi
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3 }}>
        {/* Stats Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Box sx={{ 
              p: 2, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {totalComments}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Toplam Yorum
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Box sx={{ 
              p: 2, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {comments.filter(c => c.isHidden).length}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Gizli Yorum
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Box sx={{ 
              p: 2, 
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {comments.filter(c => c.reportCount > 0).length}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Raporlu Yorum
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Comments Table */}
        <Box sx={{ 
          bgcolor: 'white', 
          borderRadius: 3, 
          boxShadow: 0,
          border: '1px solid #e2e8f0'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kullanıcı</TableCell>
                  <TableCell>Yorum</TableCell>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Beğeni</TableCell>
                  <TableCell>Rapor</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comments.map((comment: Comment) => (
                  <TableRow key={comment.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar 
                          src={comment.userPhotoURL} 
                          sx={{ width: 32, height: 32 }}
                        >
                          <Person />
                        </Avatar>
                        <Typography variant="body2">
                          {comment.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {comment.text.length > 100 
                          ? `${comment.text.substring(0, 100)}...` 
                          : comment.text
                        }
                      </Typography>
                      {comment.parentCommentId && (
                        <Chip label="Yanıt" size="small" color="secondary" sx={{ mt: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTime sx={{ fontSize: 16 }} />
                        <Typography variant="body2">
                          {formatDate(comment.createdAt)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ThumbUp sx={{ fontSize: 16 }} />
                        <Typography variant="body2">
                          {comment.likeCount}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={comment.reportCount} 
                        color={comment.reportCount > 0 ? 'error' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={comment.isHidden ? 'Gizli' : 'Görünür'}
                        color={comment.isHidden ? 'error' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleCommentVisibility(comment)}
                          color={comment.isHidden ? 'success' : 'warning'}
                        >
                          {comment.isHidden ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedComment(comment);
                            setDeleteDialogOpen(true);
                          }}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalComments}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e: any, newPage: any) => setPage(newPage)}
            onRowsPerPageChange={(e: any) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Box>
      </Container>

      {/* Delete Comment Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Yorum Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu yorumu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
          {selectedComment && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Kullanıcı:</strong> {selectedComment.username}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Yorum:</strong> {selectedComment.text}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteComment} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommentsManagementPage;
